import { supabase } from './supabase';

// Reglas de la organización (tolerancias, tardanzas, horas). Fuente de verdad: el servidor (my_policy).
// Estos valores por defecto solo se usan hasta que carga la política real.
export const DEFAULT_POLICY = {
  lateness: { tolerance_minutes: 15, mode: 'all', rounding_minutes: 1 },
  hours: { diff_margin_minutes: 5, recover_late_time: true, daily_hours: 8, month_days: 20 },
  payroll: { absence_factor: 1, minutes_debt_deduct_default: false, late_free_count: 0, late_fixed_amount: 0, justified_deduct_pct: 0, justification_requires_certificate: false },
  overtime: { weekday_multiplier: 1, weekend_multiplier: 1, holiday_multiplier: 2, night_enabled: false, night_start: '21:00', night_end: '06:00', night_multiplier: 1.5, rules: [], auto: true, min_minutes: 30, alert_enabled: true, alert_minutes: 120 },
};

let current = DEFAULT_POLICY;
let tz = 'America/Argentina/Buenos_Aires';

export const getPolicy = () => current;
export const setPolicy = (p) => {
  current = p ? { lateness: { ...DEFAULT_POLICY.lateness, ...p.lateness }, hours: { ...DEFAULT_POLICY.hours, ...p.hours }, payroll: { ...DEFAULT_POLICY.payroll, ...p.payroll }, overtime: { ...DEFAULT_POLICY.overtime, ...p.overtime } } : DEFAULT_POLICY;
};
export const setOrgTimezone = (z) => { if (z) tz = z; };
export const getOrgTimezone = () => tz;

export const loadMyPolicy = async () => {
  const { data, error } = await supabase.rpc('my_policy');
  if (error) throw error;
  setPolicy(data);
  return current;
};
export const saveMyPolicy = async (p) => {
  const { data, error } = await supabase.rpc('set_my_policy', { p });
  if (error) throw error;
  setPolicy(data);
  return current;
};

// Misma fórmula que calc_late() del servidor.
// raw = minutos entre el inicio del horario y la llegada; schedTol = tolerancia propia del horario (null = usa la de la organización; 0 = sin tolerancia)
export const calcLate = (raw, schedTol = null, pol = current) => {
  const L = pol.lateness;
  const tol = schedTol != null ? Number(schedTol) : L.tolerance_minutes;
  if (!raw || raw <= tol) return 0;
  const v = L.mode === 'subtract' ? raw - tol : raw;
  const step = Math.max(1, L.rounding_minutes || 1);
  return Math.ceil(v / step) * step;
};

// Minutos desde medianoche, en la zona horaria de la organización
export const minutesInOrgTz = (iso) => {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(iso));
  const h = Number(parts.find((x) => x.type === 'hour').value);
  const m = Number(parts.find((x) => x.type === 'minute').value);
  return h * 60 + m;
};

// Descuento por ausencias: sueldo × (ausentes / días programados) × factor de la organización
export const absenceDeduction = (base, absent, scheduled, pol = current) =>
  scheduled > 0 ? Math.round(base * (absent / scheduled) * pol.payroll.absence_factor) : 0;

// Descuento fijo por llegadas tarde (después de las gratuitas)
export const lateDeduction = (lateCount, pol = current) =>
  Math.max(0, (lateCount || 0) - pol.payroll.late_free_count) * pol.payroll.late_fixed_amount;

// Feriados del calendario de la organización (los carga el panel); sirven para sugerir el recargo
let holidayDates = new Set();
export const setHolidayDates = (arr) => { holidayDates = new Set(arr || []); };

// Multiplicador sugerido para horas extra según el día: feriado del calendario > sábado/domingo > día hábil
export const suggestedOvertimeMult = (dateStr, pol = current) => {
  const o = pol.overtime;
  if (holidayDates.has(dateStr)) return Number(o.holiday_multiplier ?? 2);
  const d = new Date(`${dateStr}T12:00:00`).getDay();
  if (d === 6) return Number(o.saturday_multiplier ?? o.weekend_multiplier);
  if (d === 0) return Number(o.sunday_multiplier ?? o.weekend_multiplier);
  return Number(o.weekday_multiplier);
};

// % del día que se descuenta por una falta justificada: el del empleado si tiene uno propio, si no el de la organización
export const justifiedPct = (emp, pol = current) => {
  const v = emp?.justified_deduct_pct;
  return Number(v ?? pol.payroll.justified_deduct_pct ?? 0) || 0;
};
